import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './style1.css';

interface VideoItem {
    id: number;
    name: string;
    url: string;
}

interface VideosProps {
    username?: string;
    onLogout?: () => void;
}

const navLinks = [
    { name: 'HOME', href: '/' },
    { name: 'PROGRAMS', href: '/programs' },
    { name: 'VIDEOS', href: '/videos' },
    { name: 'IMAGES', href: '/images' },
    { name: 'DEMOS', href: '/demos' },
    { name: 'CERTIFICATES', href: '/certificates' },
    { name: 'ABOUT', href: '/about' },
];

// Use environment variable for API base URL in production
const API_BASE =
    process.env.NODE_ENV === 'production'
        ? (process.env.REACT_APP_API_BASE || 'https://timlweb.com')
        : '';

const Videos: React.FC<VideosProps> = ({ username, onLogout }) => {
    const location = useLocation();
    const [videos, setVideos] = useState<VideoItem[]>([]);
    const [selectedVideo, setSelectedVideo] = useState<VideoItem | null>(null);
    const [videoBlobUrl, setVideoBlobUrl] = useState<string | null>(null);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('token');
        fetch(`${API_BASE}/allvideos`, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
        })
            .then(res => res.json())
            .then(data => {
                setVideos(data);

                // Auto-select video if coming from Programs page
                if (location.state && location.state.autoSelectProgram) {
                    const autoVideo = data.find(
                        (v: VideoItem) => v.name === location.state.autoSelectProgram
                    );
                    if (autoVideo) {
                        handleSelectVideo(autoVideo);
                    }
                }
            })
            .catch(() => setVideos([]));
        // eslint-disable-next-line
    }, []);

    const handleSelectVideo = async (video: VideoItem) => {
        setSelectedVideo(video);
        setVideoBlobUrl(null);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${API_BASE}${video.url}`,
                {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
                }
            );
            if (!response.ok) throw new Error('Failed to fetch video');
            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setVideoBlobUrl(url);
        } catch {
            setVideoBlobUrl(null);
        }
    };

    const handleClosePlayer = () => {
        setSelectedVideo(null);
        if (videoBlobUrl) {
            URL.revokeObjectURL(videoBlobUrl);
            setVideoBlobUrl(null);
        }
    };

    return (
        <div className="modern-layout">
            <header className="modern-header">
                <div className="header-container">
                    <nav className={`modern-nav ${menuOpen ? 'open' : ''}`}>
                        {navLinks.map(link =>
                            link.href.startsWith('/')
                                ? <Link
                                    key={link.name}
                                    to={link.href}
                                    className="modern-nav-link"
                                    style={{ textDecoration: 'none' }}
                                >
                                    {link.name}
                                </Link>
                                : <a
                                    key={link.name}
                                    href={link.href}
                                    className="modern-nav-link"
                                >
                                    {link.name}
                                </a>
                        )}
                        <button className="logout-btn" onClick={onLogout}>LOG OUT</button>
                    </nav>
                    <button
                        className="menu-toggle"
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label="Toggle menu"
                    >
                        <span className="menu-icon">&#9776;</span>
                    </button>
                </div>
            </header>
            <main className="modern-content">
                <h2 style={{ textAlign: 'center' }}>Videos</h2>
                {!selectedVideo ? (
                    <ul style={{ listStyle: 'none', padding: 0, width: '100%' }}>
                        {[...videos].sort((a, b) => b.id - a.id).map(video => (
                            <li key={video.id} style={{ margin: '0.5rem 0' }}>
                                <button
                                    style={{
                                        width: '100%',
                                        padding: '0.7rem 1rem',
                                        borderRadius: '12px',
                                        border: '1px solid #2575fc44',
                                        background: '#fff',
                                        color: '#2575fc',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        fontSize: '1rem',
                                        transition: 'background 0.2s',
                                        font: 'inherit'
                                    }}
                                    onClick={() => handleSelectVideo(video)}
                                >
                                    {video.name}
                                </button>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div style={{ marginTop: '2rem', width: '100%', textAlign: 'center' }}>
                        <button
                            style={{
                                marginBottom: '1rem',
                                padding: '0.5rem 1rem',
                                borderRadius: '12px',
                                border: 'none',
                                background: '#2575fc',
                                color: '#fff',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                font: 'inherit',
                                width: '100%',
                                maxWidth: '500px'
                            }}
                            onClick={handleClosePlayer}
                        >
                            Close Player
                        </button>
                        <h3>{selectedVideo.name}</h3>
                        {videoBlobUrl ? (
                            <video
                                src={videoBlobUrl}
                                controls
                                autoPlay
                                muted
                                style={{
                                    width: '100%',
                                    maxWidth: '500px',
                                    borderRadius: '16px',
                                    boxShadow: '0 2px 16px #6a11cb22'
                                }}
                            />
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', margin: '2rem 0' }}>
                                <div className="video-spinner" />
                                <div>Loading video...</div>
                            </div>
                        )}
                    </div>
                )}
            </main>
            <footer className="modern-footer">
                &copy; {new Date().getFullYear()} Timo
            </footer>
        </div>
    );
};

export default Videos;